import { includes, isNil } from "lodash";
import {
    PaymentMethodName,
    PaymentRequestStatus,
} from "../../../PaymentContainerClient/commons/enums";
import DocumentDto from "../../../PaymentContainerClient/dtos/DocumentDto";
import PaymentRequestDto from "../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import { hasExpired } from "../../commons/calculators";
import { PrivacyCookiePolicyMenu } from "../PrivacyCookiePolicyMenu";
import { Tabs } from "../Tabs";
import { DocumentsPreview } from "./DocumentsPreview";
import { PaymentMethods } from "./PaymentMethods";
import { PaymentPlans } from "./PaymentPlans";
import "./style.css";
import womanExclamation from "./womanExclamation.svg";

const fm = getFormattedMessageWithScope("components.PaymentForm");

interface PaymentFormProps {
    paymentRequest?: PaymentRequestDto;
    error?: any;
    currentStep: number;
    onChangeStep: Function;
    onSetPayNow: Function;
    onChangePaymentPlanId: Function;
    selectedPaymentPlanId?: string;
    onAddInstallmentId: Function;
    onRemoveInstallmentId: Function;
    selectedInstallmentIds: string[];
    onChangePaymentMethod: Function;
    selectedPaymentMethod?: PaymentMethodName;
    onOpenDocumentsPreviewer: Function;
    selectedDocumentForPreview?: DocumentDto;
    isPayNow: boolean;
    isDownloadingDocument: boolean;
    isDocumentsPreviewerOpen: boolean;
    onCloseDocumentsPreviewer: Function;
    onDownloadDocument: Function;
    documentIdDownloading?: string;
}

export const PaymentForm = ({
    paymentRequest,
    error,
    currentStep,
    onChangeStep,
    onSetPayNow,
    onChangePaymentPlanId,
    selectedPaymentPlanId,
    onAddInstallmentId,
    onRemoveInstallmentId,
    selectedInstallmentIds,
    onChangePaymentMethod,
    selectedPaymentMethod,
    onOpenDocumentsPreviewer,
    selectedDocumentForPreview,
    isPayNow,
    isDownloadingDocument,
    isDocumentsPreviewerOpen,
    onCloseDocumentsPreviewer,
    onDownloadDocument,
    documentIdDownloading,
}: PaymentFormProps) => {
    const isPaidOrExternalPaidOrScheduled = includes(
        [
            PaymentRequestStatus.Paid,
            PaymentRequestStatus.ExternalPaid,
            PaymentRequestStatus.Scheduled,
        ],
        paymentRequest?.status
    );

    return (
        <div className="c-PaymentForm-container">
            <div className="c-PaymentForm-tabs-container">
                <Tabs
                    items={[
                        {
                            title: fm("firstTab"),
                            onClick: () => {
                                onChangeStep(0);
                            },
                            active: true,
                            current: currentStep === 0,
                        },
                        {
                            title: fm("secondTab"),
                            onClick: () => {
                                onChangeStep(1);
                            },
                            active: currentStep >= 1,
                            disabled: currentStep === 0,
                            current: currentStep === 1,
                        },
                        {
                            title: fm("thirdTab"),
                            onClick: () => {
                                onChangeStep(2);
                            },
                            active: currentStep === 2,
                            disabled: currentStep <= 1,
                            current: currentStep === 2,
                        },
                    ]}
                    disabled={
                        !isNil(error) ||
                        (paymentRequest && hasExpired(paymentRequest)) ||
                        isPaidOrExternalPaidOrScheduled
                    }
                />
            </div>
            {paymentRequest && !hasExpired(paymentRequest) ? (
                <div className="c-PaymentForm-tabs-content">
                    {currentStep === 0 ? (
                        <DocumentsPreview
                            paymentRequest={paymentRequest}
                            onOpenDocumentsPreviewer={onOpenDocumentsPreviewer}
                            selectedDocumentForPreview={
                                selectedDocumentForPreview
                            }
                            isDownloadingDocument={isDownloadingDocument}
                            isDocumentsPreviewerOpen={isDocumentsPreviewerOpen}
                            onCloseDocumentsPreviewer={
                                onCloseDocumentsPreviewer
                            }
                            onDownloadDocument={onDownloadDocument}
                            documentIdDownloading={documentIdDownloading}
                        />
                    ) : currentStep === 1 ? (
                        <PaymentPlans
                            paymentRequest={paymentRequest}
                            onChangePaymentPlanId={onChangePaymentPlanId}
                            selectedPaymentPlanId={selectedPaymentPlanId}
                            onAddInstallmentId={onAddInstallmentId}
                            onRemoveInstallmentId={onRemoveInstallmentId}
                            selectedInstallmentIds={selectedInstallmentIds}
                            onSetPayNow={onSetPayNow}
                            isPayNow={isPayNow}
                        />
                    ) : (
                        <PaymentMethods
                            paymentRequest={paymentRequest}
                            onChangePaymentMethod={onChangePaymentMethod}
                            selectedPaymentMethod={selectedPaymentMethod}
                            selectedInstallmentIds={selectedInstallmentIds}
                            isPayNow={isPayNow}
                        />
                    )}
                </div>
            ) : (
                <>
                    <PrivacyCookiePolicyMenu />
                    <div className="c-PaymentForm-error-container">
                        <img alt="woman" src={womanExclamation} />
                        <p className="c-PaymentForm-error-value">
                            {fm(
                                error
                                    ? error.message ===
                                      "DisabledPaymentRequestError"
                                        ? "disabledPaymentRequestError"
                                        : "genericError"
                                    : paymentRequest &&
                                      hasExpired(paymentRequest)
                                    ? "expiredPaymentRequestError"
                                    : "genericError"
                            )}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};
